import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { MatPaginator } from '@angular/material/paginator';
import { OverviewDataSource } from '../../../overview/overview-data-source';
import { catchError, mergeMap, takeUntil } from 'rxjs/operators';
import { EMPTY, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { SelectedCustomerService } from '../../services/selected-customer.service';
import { OverlayDialogHelper } from '../../utils/overlay-dialog.helper';
import moment from 'moment';
import { PriceSummaryDialogComponent } from './price-summary-dialog/price-summary-dialog.component';
import { DOCUMENT } from '@angular/common';
import { openQuoteCreationDialog } from '../quote-creation-dialog/quote-creation-dialog.component';
import { OverviewDataService, QuoteItem } from '../../../overview/overview.data.service';
import { openQuoteDeleteDialog } from '../quote-delete-dialog/quote-delete-dialog.component';
import { ImageDialogComponent, ImageDialogData } from '../templates-table/image-dialog/image-dialog.component';
import { AppService } from '../../services/app.service';
import { PaginationService } from '../../services/paginator.service';
import { QuoteStatus } from '../../models/quote.model';

const CANCEL_DEFAULT_REASON = '19';

interface BaseQuoteEvent {
    id: string;
    status?: QuoteStatus;
}

export interface ViewQuoteEvent extends BaseQuoteEvent {
    quoteId: string;
}

export interface CopyQuoteEvent extends BaseQuoteEvent {
    customerPurchaseNumber: string;
}

export interface DeleteQuoteEvent extends BaseQuoteEvent {
    reason: string;
}

export interface ConvertQuoteEvent extends BaseQuoteEvent {
    templateDescription?: string;
    primarySchemes?: string;
}

@Component({
    selector: 'app-overview-table',
    templateUrl: './overview-table.component.html',
    styleUrls: ['./overview-table.component.scss'],
})
export class OverviewTableComponent implements OnInit, AfterViewInit, OnDestroy {
    constructor(
        @Inject(DOCUMENT) document: Document,
        private translationService: TranslateService,
        private selectedCustomerService: SelectedCustomerService,
        private dialog: MatDialog,
        private dataService: OverviewDataService,
        private appService: AppService,
        public paginatorService: PaginationService
    ) {
        this.helper = new OverlayDialogHelper<PriceSummaryDialogComponent, QuoteItem>(
            document,
            this.dialog,
            PriceSummaryDialogComponent,
            'price-summary-dialog'
        );
        this.viewer = new OverlayDialogHelper<ImageDialogComponent, ImageDialogData>(
            document,
            dialog,
            ImageDialogComponent,
            'image-dialog'
        );
    }

    public get isPartnerMode() {
        return !!this.selectedCustomerService.customerForPartnerMode$?.value;
    }

    private unsubscribe$: Subject<void> = new Subject<void>();
    private helper: OverlayDialogHelper<PriceSummaryDialogComponent, QuoteItem>;
    private viewer: OverlayDialogHelper<ImageDialogComponent, ImageDialogData>;
    private today: moment.Moment;

    @ViewChild(MatSort, { static: true })
    public sort: MatSort;

    @Input()
    public dataSource: OverviewDataSource<QuoteItem, any>;
    @Input()
    public isEmployee: boolean;
    @Input()
    public isAdmin: boolean;
    @Input() defaultSort = 'createdDate_desc';

    @Output()
    public view = new EventEmitter<ViewQuoteEvent>();
    @Output()
    public copyQuote = new EventEmitter<CopyQuoteEvent>();
    @Output()
    public delete = new EventEmitter<DeleteQuoteEvent>();
    @Output()
    public pagingError = new EventEmitter<any>();
    @Output()
    public sortError = new EventEmitter<any>();

    public columnsToDisplayForCustomer = [
        'status',
        'createdDate',
        'schemePreview',
        'title',
        'totalGross',
        'creatorFullName',
        'status',
        'actions',
    ];
    public columnsToDisplayForEmployee = [
        'status',
        'documentId',
        'title',
        'schemePreview',
        'createdDate',
        'creator',
        'totalGross',
        'actions',
    ];
    public columnsToDisplay;

    @ViewChild(MatPaginator, { static: true })
    public paginator: MatPaginator;

    public isNew(obj: QuoteItem) {
        return obj.status === QuoteStatus.new;
    }

    public ngOnInit() {
        this.today = moment.utc().startOf('day');
        this.setColumnsToDisplay();
        this.dataSource.init(this.paginator);
        this.selectedCustomerService.customerForPartnerMode$.subscribe(() => this.setColumnsToDisplay());
        this.appService.salesOrg$.subscribe(() => this.setColumnsToDisplay());
        this.dataSource.loadOverviewData({
            parameters: {
                sortBy: 'createdDate',
                sortOrder: 'desc',
            },
        });
    }

    private setColumnsToDisplay() {
        this.columnsToDisplay = [
            ...(this.isEmployee && !this.isPartnerMode
                ? this.columnsToDisplayForEmployee
                : this.columnsToDisplayForCustomer),
        ];
    }

    public ngAfterViewInit(): void {
        // listen to paging events
        this.paginator.page
            .pipe(
                mergeMap(() => this.dataSource.loadOverviewData()),
                catchError(() => {
                    this.pagingError.emit();
                    return EMPTY;
                })
            )
            .subscribe();
    }

    public onViewClick(event: ViewQuoteEvent) {
        this.view.emit(event);
    }

    public onCopyClick(quote: QuoteItem) {
        openQuoteCreationDialog(this.dialog, {
            title: this.translationService.instant('OVERVIEW.DIALOG.COPY_OFFER.TITLE'),
            confirm: this.translationService.instant('COMMON.DIALOG.CONFIRM'),
            cancel: this.translationService.instant('COMMON.DIALOG.CANCEL'),
            quoteTitle:
                this.translationService.instant('OVERVIEW.DIALOG.COPY_OFFER.COPY') + ` ${quote.customerPurchaseNumber}`,
        }).subscribe((customerPurchaseNumber) => {
            if (customerPurchaseNumber) {
                this.copyQuote.emit({
                    ...quote,
                    customerPurchaseNumber,
                });
            }
        });
    }

    public onDeleteClick(quote: QuoteItem) {
        let reasons$: Subject<{ [key: string]: string }>;
        if (this.isEmployee && quote.status !== QuoteStatus.new) {
            reasons$ = new Subject<{ [key: string]: string }>();
            this.dataService.getAvailableCancellationReasons().pipe(takeUntil(this.unsubscribe$)).subscribe(reasons$);
        }

        openQuoteDeleteDialog(
            this.dialog,
            this.translationService.instant('OVERVIEW.DIALOG.DELETE_OFFER.TITLE'),
            this.translationService.instant('OVERVIEW.DIALOG.DELETE_OFFER.DESCRIPTION'),
            `${this.translationService.instant('OVERVIEW.DIALOG.DELETE_OFFER.MESSAGE')} ${
                quote.customerPurchaseNumber
            }`,
            this.translationService.instant('COMMON.DIALOG.CONFIRM'),
            this.translationService.instant('COMMON.DIALOG.CANCEL'),
            reasons$
        ).subscribe((reason) => {
            reason = reason === true ? CANCEL_DEFAULT_REASON : reason;
            if (reason) {
                this.delete.emit({
                    ...quote,
                    reason,
                });
            }
        });
    }

    public handlePriceSummaryEvent(event: MouseEvent, obj: QuoteItem) {
        if (obj.totalGross || obj.totalAdvantage || obj.totalPrice) {
            this.helper.handleEvent(event, obj);
        }
    }

    public getStatus(item: QuoteItem) {
        if (
            item.status === QuoteStatus.quoted &&
            item.bindingTo &&
            moment.utc(item.bindingTo).startOf('day').add(1, 'day').isSameOrBefore(this.today)
        ) {
            return QuoteStatus.expired;
        }
        return item.status?.toUpperCase();
    }

    public isViGuidePlanning({ externalApp }: QuoteItem) {
        return externalApp?.toLowerCase()?.replace(/\s/g, '') === 'viguideplanning';
    }

    public onImageHover(event: MouseEvent, obj: QuoteItem) {
        if (obj.status !== QuoteStatus.new) {
            this.viewer.handleEvent(event, {
                id: obj.id,
                imageUrl: obj.scheme?.preview,
            });
        }
    }

    getTotalPages(): number {
        return this.paginatorService.getTotalPages(this.paginator);
    }

    goToFirstPage() {
        this.paginatorService.goToFirstPage(this.paginator);
    }

    goToPreviousPage() {
        this.paginatorService.goToPreviousPage(this.paginator);
    }

    goToNextPage() {
        this.paginatorService.goToNextPage(this.paginator);
    }

    goToLastPage() {
        this.paginatorService.goToLastPage(this.paginator);
    }

    public ngOnDestroy() {
        this.helper.destroy();
    }
}
